import { useState } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import {
  Smartphone, CheckCircle2, ArrowRight, X,
  Home, Shield, User, Download, CreditCard, Copy, Plug,
  Lightbulb, Check, Circle, Zap, RefreshCw, MessageCircle,
} from 'lucide-react';
import { AppLang } from '../i18n';
import './Onboarding.css';

interface OnboardingProps {
  lang: AppLang;
  onComplete: () => void;
}

type TabId = 'home' | 'vpn' | 'profile' | 'support' | null;

interface Slide {
  id: string;
  step: number | null;
  activeTab: TabId;
  title_ru: string;
  title_en: string;
  subtitle_ru: string;
  subtitle_en: string;
}

/* ─── Visuals ─── */

const ROADMAP_STEPS = [
  { icon: <Download size={15} />, label_ru: 'Скачать Happ',      label_en: 'Download Happ',   color: '#FFDD2D' },
  { icon: <CreditCard size={15} />, label_ru: 'Оформить тариф',   label_en: 'Choose a plan',    color: '#FFDD2D' },
  { icon: <Copy size={15} />,       label_ru: 'Получить ключ',    label_en: 'Get your key',    color: '#FFDD2D' },
  { icon: <Plug size={15} />,       label_ru: 'Вставить в Happ',  label_en: 'Paste in Happ',   color: '#FFDD2D' },
  { icon: <CheckCircle2 size={15} />, label_ru: 'VPN включён!', label_en: 'VPN is on!', color: '#4ade80' },
];

const VisualWelcome = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-welcome" style={{ justifyContent: 'flex-start', paddingTop: 4 }}>
    <div className="ob-roadmap">
      {ROADMAP_STEPS.map((s, i) => (
        <div key={i} className="ob-rm-row">
          <div className="ob-rm-left">
            <div className="ob-rm-circle" style={{ borderColor: s.color, color: s.color }}>
              {i === 4 ? <CheckCircle2 size={13} /> : <span>{i + 1}</span>}
            </div>
            {i < 4 && <div className="ob-rm-vline" />}
          </div>
          <div className={`ob-rm-card ${i === 4 ? 'ob-rm-card--done' : ''}`}>
            <div className="ob-rm-card-icon" style={{ color: s.color }}>{s.icon}</div>
            <span className="ob-rm-card-label">{ru ? s.label_ru : s.label_en}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const VisualDownload = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-screen">
    <div className="ob-mock-card ob-mock-download">
      <div className="ob-app-icon">
        <Smartphone size={32} strokeWidth={1.5} />
      </div>
      <div className="ob-app-name">Happ</div>
      <div className="ob-app-desc-text">{ru ? 'VPN-приложение для iOS и Android' : 'VPN app for iOS and Android'}</div>
      <div className="ob-store-btns">
        <div className="ob-store-btn ob-store-apple">
          <span className="ob-store-icon"></span>
          <div className="ob-store-text">
            <span className="ob-store-sub">{ru ? 'Загрузить в' : 'Download on the'}</span>
            <span className="ob-store-name">App Store</span>
          </div>
        </div>
        <div className="ob-store-btn ob-store-google">
          <span className="ob-store-icon">▶</span>
          <div className="ob-store-text">
            <span className="ob-store-sub">{ru ? 'Доступно в' : 'Get it on'}</span>
            <span className="ob-store-name">Google Play</span>
          </div>
        </div>
      </div>
      <div className="ob-highlight-ring" />
    </div>
    <div className="ob-tip-box">
      <Lightbulb size={14} style={{ flexShrink: 0 }} /> {ru ? 'Бот пришлёт ссылку на скачивание автоматически' : 'The bot will send you a download link automatically'}
    </div>
  </div>
);

const VisualPlan = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-screen">
    <div className="ob-mock-card ob-mock-wallet">
      <div className="ob-mock-balance-label"><CreditCard size={13} />{ru ? 'Тарифы' : 'Plans'}</div>
      <div className="ob-highlight-ring" />
    </div>
    <div className="ob-amount-row">
      {[
        { label: ru ? '1 мес' : '1 mo', price: '299₽' },
        { label: ru ? '3 мес' : '3 mo', price: '749₽' },
        { label: ru ? '12 мес' : '12 mo', price: '2090₽' },
      ].map((p, i) => (
        <div key={p.label} className={`ob-amount-chip ${i === 1 ? 'ob-amount-chip--active' : ''}`}>
          {p.label} — {p.price}
        </div>
      ))}
    </div>
    <div className="ob-tip-box">
      <Lightbulb size={14} style={{ flexShrink: 0 }} /> {ru ? 'Чем дольше тариф, тем дешевле в день' : 'The longer the plan, the cheaper per day'}
    </div>
  </div>
);

const VisualCopyKey = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-screen">
    <div className="ob-mock-card ob-mock-connect">
      <div className="ob-mock-connect-btn">
        <Smartphone size={18} />
        <span>{ru ? 'Подключиться' : 'Connect'}</span>
        <ArrowRight size={16} />
      </div>
      <div className="ob-key-box">
        <div className="ob-key-label"><Copy size={12} />{ru ? 'Твой ключ' : 'Your key'}</div>
        <div className="ob-key-value">vless://abc123…<span className="ob-key-fade" /></div>
        <div className="ob-key-copied"><Check size={14} /> {ru ? 'Скопирован' : 'Copied'}</div>
      </div>
      <div className="ob-highlight-ring" />
    </div>
    <div className="ob-steps-mini">
      <div className="ob-step-mini"><span>1</span>{ru ? 'Нажми «Подключиться»' : 'Tap "Connect"'}</div>
      <div className="ob-step-mini"><span>2</span>{ru ? 'Бот пришлёт кнопку' : 'Bot sends a button'}</div>
      <div className="ob-step-mini"><span>3</span>{ru ? 'Нажми в боте — Happ откроется' : 'Tap in bot — Happ opens'}</div>
    </div>
  </div>
);

const VisualPasteKey = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-screen">
    <div className="ob-mock-card ob-mock-happ">
      <div className="ob-happ-header">
        <Smartphone size={16} />
        <span>Happ</span>
      </div>
      <div className="ob-happ-paste-area">
        <div className="ob-happ-paste-icon"><Plug size={24} strokeWidth={1.5} /></div>
        <div className="ob-happ-paste-label">Import from Clipboard</div>
        <div className="ob-happ-paste-hint">{ru ? 'Ключ уже в буфере обмена' : 'Key is already copied'}</div>
      </div>
      <div className="ob-highlight-ring" />
    </div>
    <div className="ob-tip-box">
      <Lightbulb size={14} style={{ flexShrink: 0 }} /> {ru ? 'Ключ уже скопирован — просто нажми Import' : 'Key is already copied — just tap Import'}
    </div>
  </div>
);

const VisualDone = ({ ru }: { ru: boolean }) => (
  <div className="ob-visual ob-visual-done">
    <motion.div
      className="ob-done-circle"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 159, damping: 20, delay: 0.1 }}
    >
      <CheckCircle2 size={60} strokeWidth={1.5} />
    </motion.div>
    <div className="ob-done-features">
      <div className="ob-done-feat"><Circle size={14} fill="#4ade80" stroke="#4ade80" /> {ru ? 'VPN активен — трафик зашифрован' : 'VPN active — traffic encrypted'}</div>
      <div className="ob-done-feat"><Zap size={14} /> {ru ? 'Скорость не ограничена' : 'No speed limits'}</div>
      <div className="ob-done-feat"><RefreshCw size={14} /> {ru ? 'Ключ один — все устройства' : 'One key — all devices'}</div>
      <div className="ob-done-feat"><CreditCard size={14} /> {ru ? 'Тарифы от 299₽/мес' : 'Plans from 299₽/mo'}</div>
    </div>
  </div>
);

/* ─── Slides metadata ─── */

const SLIDES: Slide[] = [
  {
    id: 'welcome',
    step: null,
    activeTab: null,
    title_ru: 'Как пользоваться',
    title_en: 'How it works',
    subtitle_ru: 'Всего 4 шага — и VPN работает на всех твоих устройствах',
    subtitle_en: '4 simple steps — and VPN works on all your devices',
  },
  {
    id: 'download',
    step: 1,
    activeTab: null,
    title_ru: 'Скачай приложение Happ',
    title_en: 'Download the Happ app',
    subtitle_ru: 'Это VPN-клиент куда будет вставлен твой ключ. Бесплатно для iOS и Android.',
    subtitle_en: 'This is the VPN client where you paste your key. Free for iOS & Android.',
  },
  {
    id: 'plan',
    step: 2,
    activeTab: 'home',
    title_ru: 'Оформи тариф',
    title_en: 'Choose a plan',
    subtitle_ru: 'Выбери подходящий тариф: 1, 3, 6 или 12 месяцев. Оплата картой или Stars.',
    subtitle_en: 'Pick a plan: 1, 3, 6 or 12 months. Pay with card or Stars.',
  },
  {
    id: 'connect',
    step: 3,
    activeTab: 'vpn',
    title_ru: 'Получи свой ключ',
    title_en: 'Get your key',
    subtitle_ru: 'Во вкладке VPN нажми «Подключиться» — ключ скопируется, бот пришлёт кнопку для Happ.',
    subtitle_en: 'In the VPN tab tap "Connect" — key copies, bot sends a button to open Happ.',
  },
  {
    id: 'paste',
    step: 4,
    activeTab: null,
    title_ru: 'Вставь ключ в Happ',
    title_en: 'Paste key into Happ',
    subtitle_ru: 'Открой Happ → нажми «Import from Clipboard» — ключ уже скопирован, всё сделается само.',
    subtitle_en: 'Open Happ → tap "Import from Clipboard" — key is already copied, done automatically.',
  },
  {
    id: 'done',
    step: null,
    activeTab: null,
    title_ru: 'VPN включён!',
    title_en: 'VPN is on!',
    subtitle_ru: 'Ты защищён. Теперь TikTok, Instagram, YouTube и другие сайты открываются без ограничений.',
    subtitle_en: "You're protected. TikTok, Instagram, YouTube and other sites are now unblocked.",
  },
];

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? '69%' : '-69%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:  (dir: number) => ({ x: dir > 0 ? '-69%' : '69%', opacity: 0 }),
};

interface NavTabDef {
  id: TabId;
  icon: React.ReactNode;
  label_ru: string;
  label_en: string;
}

const NAV_TABS: NavTabDef[] = [
  { id: 'home',    icon: <Home size={22} strokeWidth={1.8} />,           label_ru: 'Главная',    label_en: 'Home' },
  { id: 'vpn',     icon: <Shield size={22} strokeWidth={1.8} />,         label_ru: 'VPN',        label_en: 'VPN' },
  { id: 'profile', icon: <User size={22} strokeWidth={1.8} />,           label_ru: 'Профиль',    label_en: 'Profile' },
  { id: 'support', icon: <MessageCircle size={22} strokeWidth={1.8} />,  label_ru: 'Поддержка',  label_en: 'Support' },
];

export const Onboarding = ({ lang, onComplete }: OnboardingProps) => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = SLIDES.length;
  const isLast = step === total - 1;
  const ru = lang !== 'en';

  const go = (next: number) => {
    if (next < 0 || next >= total) return;
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const dx = info.offset.x;
    if (dx < -60 && step < total - 1) go(step + 1);
    else if (dx > 60 && step > 0) go(step - 1);
  };

  const slide = SLIDES[step];
  const activeTab = slide.activeTab;

  const totalSteps = SLIDES.filter(s => s.step !== null).length;

  const renderVisual = (id: string) => {
    switch (id) {
      case 'welcome':  return <VisualWelcome ru={ru} />;
      case 'download': return <VisualDownload ru={ru} />;
      case 'plan':     return <VisualPlan ru={ru} />;
      case 'connect':  return <VisualCopyKey ru={ru} />;
      case 'paste':    return <VisualPasteKey ru={ru} />;
      case 'done':     return <VisualDone ru={ru} />;
      default:         return null;
    }
  };

  return (
    <motion.div
      className="ob-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Skip */}
      <button className="ob-skip" onClick={onComplete}>
        <X size={20} />
      </button>

      {/* Header: step badge + progress dots */}
      <div className="ob-header">
        <AnimatePresence mode="wait">
          {slide.step !== null ? (
            <motion.div
              key={`step-${slide.step}`}
              className="ob-step-badge"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.2 }}
            >
              <span className="ob-step-badge-num">{slide.step}</span>
              <span className="ob-step-badge-of">/ {totalSteps}</span>
            </motion.div>
          ) : (
            <motion.div
              key="step-null"
              className="ob-step-badge ob-step-badge--none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>

        {/* Progress dots */}
        <div className="ob-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`ob-dot ${i === step ? 'ob-dot-active' : ''}`}
              onClick={() => go(i)}
            />
          ))}
        </div>
      </div>

      {/* Slide area */}
      <div className="ob-slides-wrap">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={slide.id}
            className="ob-slide"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 199, damping: 32 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={handleDragEnd}
          >
            {/* Visual */}
            <div className="ob-visual-wrap">{renderVisual(slide.id)}</div>

            {/* Text */}
            <div className="ob-text">
              <h2 className="ob-title">{ru ? slide.title_ru : slide.title_en}</h2>
              <p className="ob-subtitle">{ru ? slide.subtitle_ru : slide.subtitle_en}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom buttons */}
      <div className="ob-bottom">
        <motion.button
          className="ob-btn-next"
          onClick={() => isLast ? onComplete() : go(step + 1)}
          whileTap={{ scale: 0.97 }}
        >
          {isLast
            ? (ru ? 'Начать!' : "Let's go!")
            : step === 0
              ? (ru ? 'Начать инструкцию' : 'Start guide')
              : (ru ? 'Следующий шаг' : 'Next step')}
          <ArrowRight size={16} style={{ marginLeft: 6 }} />
        </motion.button>

        {!isLast && (
          <button className="ob-btn-skip" onClick={onComplete}>
            {ru ? 'Пропустить инструкцию' : 'Skip guide'}
          </button>
        )}
      </div>

      {/* Fake nav bar */}
      <nav className="ob-fake-nav">
        {NAV_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id as string}
              className={`ob-fake-tab ${isActive ? 'ob-fake-tab--active' : ''}`}
            >
              {isActive ? (
                <motion.div
                  className="ob-fake-pill"
                  layoutId="obNavPill"
                  transition={{ type: 'spring', stiffness: 309, damping: 35 }}
                >
                  {tab.icon}
                  <span className="ob-fake-pill-label">
                    {ru ? tab.label_ru : tab.label_en}
                  </span>
                </motion.div>
              ) : (
                <div className="ob-fake-icon">{tab.icon}</div>
              )}
            </div>
          );
        })}
      </nav>
    </motion.div>
  );
};
